import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Button,
    Table,
    Badge,
    FormGroup,
    Label,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { Download, Users, Wallet, TrendingUp, Calendar, Search, Award, Activity, UserMinus, UserCheck, AlertCircle, Filter } from "lucide-react";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import {
    hasPagePermissionWithAdmin,
    isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { getCustomerSegregationReport, exportCustomerSegregationReportToExcel } from "../../components/Api/GmsCustomerSegregationReport";
import CustomerProfileModal from "../../components/CustomerProfileModal";

// Format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Format date for display
const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "N/A";
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (error) {
        return "N/A";
    }
};

const getSegmentColor = (segment) => {
    if (segment.includes("VIP")) return "primary";
    if (segment.includes("Repeat")) return "success";
    if (segment.includes("Recent")) return "info";
    if (segment.includes("One-Time")) return "warning";
    if (segment.includes("Sleeping")) return "dark";
    if (segment.includes("Lost")) return "danger";
    return "secondary";
};

const getSegmentIcon = (segment, size = 18) => {
    if (segment.includes("VIP")) return <Award size={size} className="me-2 text-primary" />;
    if (segment.includes("Repeat")) return <TrendingUp size={size} className="me-2 text-success" />;
    if (segment.includes("Recent")) return <UserCheck size={size} className="me-2 text-info" />;
    if (segment.includes("One-Time")) return <Activity size={size} className="me-2 text-warning" />;
    if (segment.includes("Sleeping")) return <AlertCircle size={size} className="me-2 text-dark" />;
    if (segment.includes("Lost")) return <UserMinus size={size} className="me-2 text-danger" />;
    return <Users size={size} className="me-2 text-secondary" />;
};

const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      borderColor: state.isFocused ? '#556ee6' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      maxHeight: 200,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 180,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:hover': {
        backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
      },
    }),
};

const SEGMENTS_LIST = [
    "High Value (VIP)",
    "Repeat",
    "Recent / New",
    "One-Time",
    "Sleeping",
    "Lost"
];

const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
        companyId: authUser.companyId || user.user_Company_Id || null,
        branchId: authUser.branchId || user.user_Branch_Id || null,
    };
};

export default function CustomerSegregationReportPage() {
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [reportData, setReportData] = useState({ summary: [], details: [] });
    const [showCustomerProfile, setShowCustomerProfile] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSegment, setSelectedSegment] = useState(null);

    // Filtered Summary ensures all 6 are shown
    const summaryToDisplay = useMemo(() => {
        return SEGMENTS_LIST.map(segmentName => {
            const data = reportData.summary.find(s => s.segment === segmentName);
            return {
                segment: segmentName,
                customerCount: data ? data.customerCount : 0,
                totalRevenue: data ? data.totalRevenue : 0
            };
        });
    }, [reportData.summary]);

    // Check permissions
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsCustomerSegregationReport');

    const session = useMemo(() => getUserSession(), []);

    const [businesses, setBusinesses] = useState([]);
    const [branches, setBranches] = useState([]);
    const [businessId, setBusinessId] = useState(null);
    const [branchId, setBranchId] = useState(null);

    // Filter businesses/branches derived options
    const businessOptions = useMemo(() => {
        return businesses.map(b => ({
            value: b.business_Id,
            label: b.business_NameEn || b.business_Name
        }));
    }, [businesses]);

    const branchOptions = useMemo(() => {
        const filteredBranches = businessId
            ? branches.filter(branch => branch.branch_Buisness_Id === businessId)
            : branches;

        return filteredBranches.map(b => ({
            value: b.branch_Id,
            label: b.branch_NameEn || b.branch_Name
        }));
    }, [branches, businessId]);

    const isBusinessReadonly = !!session.companyId;
    const isBranchReadonly = !!session.branchId;

    const loadBusinesses = async () => {
        try {
            const response = await getBusinesses({ business_Active: true });
            const data = response.data || response;
            if (Array.isArray(data)) setBusinesses(data);
        } catch (error) {
            console.error("Error loading businesses:", error);
        }
    };

    const loadBranches = async (id = null) => {
        try {
            const filter = id ? { Business_Id: id, branch_Active: true } : { branch_Active: true };
            const response = await getBranches(filter);
            const data = response.data || response;
            if (Array.isArray(data)) setBranches(data);
        } catch (error) {
            console.error("Error loading branches:", error);
        }
    };

    useEffect(() => {
        if (hasPermission) {
            loadBusinesses();
        } else {
            window.location.href = '/Dashboard';
        }
    }, [hasPermission]);

    // Auto-populate business based on session
    useEffect(() => {
        if (session.companyId && businesses.length > 0) {
            const id = typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId;
            const exists = businesses.find(b => b.business_Id === id);
            if (exists) {
                setBusinessId(id);
                loadBranches(id);
            }
        }
    }, [businesses, session.companyId]);

    // Auto-populate branch based on session
    useEffect(() => {
        if (session.branchId && branches.length > 0 && businessId) {
            const id = typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId;
            const exists = branches.find(b => b.branch_Id === id && b.branch_Buisness_Id === businessId);
            if (exists) {
                setBranchId(id);
            }
        }
    }, [branches, businessId, session.branchId]);

    // If both are selected (or auto-populated), load the report
    useEffect(() => {
        if (businessId && branchId) {
            loadReport();
        } else {
            setReportData({ summary: [], details: [] });
            setSelectedSegment(null);
        }
    }, [businessId, branchId]);

    const loadReport = async () => {
        if (!businessId || !branchId) return;
        try {
            setLoading(true);
            const data = await getCustomerSegregationReport(businessId, branchId);
            setReportData(data);
        } catch (error) {
            console.error("Error loading report:", error);
            Swal.fire("Error", "Failed to load report data", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        if (!businessId || !branchId) {
            Swal.fire("Warning", "Please select Company and Branch first", "warning");
            return;
        }
        try {
            setExportLoading(true);
            await exportCustomerSegregationReportToExcel(businessId, branchId);
        } catch (error) {
            console.error("Error exporting report:", error);
            Swal.fire("Error", "Failed to export report", "error");
        } finally {
            setExportLoading(false);
        }
    };

    const filteredDetails = useMemo(() => {
        if (!selectedSegment) return [];

        return reportData.details.filter(item => {
            const matchesSearch = item.customer_NameEn.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesSegment = selectedSegment === "All" || item.segment === selectedSegment;
            return matchesSearch && matchesSegment;
        });
    }, [reportData.details, searchTerm, selectedSegment]);

    if (!hasPermission) return null;

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="Reports" breadcrumbItem="Customer Segregation Report" />

                <Row className="mb-4">
                    <Col lg={12}>
                        <Card className="border-0 shadow-sm mb-4">
                            <CardBody className="py-3">
                                <Row className="align-items-center">
                                    <Col md={3}>
                                        <FormGroup className="mb-md-0">
                                            <Label className="fw-bold"><Filter size={16} className="me-2 text-primary" />Company</Label>
                                            <Select
                                                value={businessOptions.find(opt => opt.value === businessId)}
                                                onChange={(opt) => {
                                                    setBusinessId(opt?.value || null);
                                                    setBranchId(null);
                                                    if (opt) loadBranches(opt.value);
                                                }}
                                                options={businessOptions}
                                                placeholder="Select Company..."
                                                isDisabled={isBusinessReadonly}
                                                styles={selectStyles}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup className="mb-md-0">
                                            <Label className="fw-bold"><Filter size={16} className="me-2 text-primary" />Branch</Label>
                                            <Select
                                                value={branchOptions.find(opt => opt.value === branchId)}
                                                onChange={(opt) => setBranchId(opt?.value || null)}
                                                options={branchOptions}
                                                placeholder="Select Branch..."
                                                isDisabled={isBranchReadonly || !businessId}
                                                styles={selectStyles}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex align-items-center justify-content-end gap-2 mt-4 mt-md-0 pt-2">
                                            <Button color="success" onClick={handleExport} disabled={exportLoading || loading || !businessId || !branchId} className="px-3">
                                                {exportLoading ? <ClipLoader size={16} color="#fff" /> : <Download size={18} className="me-2" />}
                                                Export
                                            </Button>
                                            <Button color="primary" onClick={loadReport} disabled={loading || !businessId || !branchId} className="px-3">
                                                {loading ? <ClipLoader size={16} color="#fff" /> : <Activity size={18} className="me-2" />}
                                                Refresh
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {!businessId || !branchId ? (
                    <Card className="border-0 shadow-sm">
                        <CardBody className="py-5 text-center">
                            <div className="mb-4">
                                <Filter size={48} className="text-muted opacity-25" />
                            </div>
                            <h4 className="text-muted">Please select Company and Branch to load report</h4>
                            <p className="text-muted mb-0">Select the filters above to view the customer segregation data.</p>
                        </CardBody>
                    </Card>
                ) : reportData.summary.length === 0 && !loading ? (
                    <Card className="border-0 shadow-sm">
                        <CardBody className="py-5 text-center">
                            <div className="mb-4">
                                <Users size={48} className="text-muted opacity-25" />
                            </div>
                            <h4 className="text-muted">No data found</h4>
                            <p className="text-muted mb-0">There are no customers matching the selected criteria.</p>
                        </CardBody>
                    </Card>
                ) : null}

                {/* Summary Cards */}
                {businessId && branchId && (
                <Row>
                    {summaryToDisplay.map((sum, index) => (
                        <Col xl={3} md={6} key={index} className="mb-4">
                            <Card
                                className={`border-0 shadow-sm h-100 ${selectedSegment === sum.segment ? 'border border-primary shadow-lg' : ''}`}
                                style={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    transform: selectedSegment === sum.segment ? 'scale(1.03)' : 'none',
                                    backgroundColor: selectedSegment === sum.segment ? '#fbfcfe' : 'white'
                                }}
                                onClick={() => setSelectedSegment(selectedSegment === sum.segment ? null : sum.segment)}
                            >
                                <CardBody>
                                    <div className="d-flex align-items-center mb-3">
                                        <div className={`avatar-sm rounded-circle bg-${getSegmentColor(sum.segment)} bg-soft text-center d-flex align-items-center justify-content-center`}>
                                            {getSegmentIcon(sum.segment, 24)}
                                        </div>
                                        <div className="ms-3">
                                            <h5 className="mb-0 text-truncate" style={{ maxWidth: '150px' }}>{sum.segment}</h5>
                                        </div>
                                    </div>
                                    <div className="row text-center">
                                        <div className="col-6 border-end">
                                            <p className="text-muted mb-1 font-size-12">Customers</p>
                                            <h4 className="mb-0">{sum.customerCount}</h4>
                                        </div>
                                        <div className="col-6">
                                            <p className="text-muted mb-1 font-size-12">Revenue</p>
                                            <h4 className="mb-0 text-success">{formatCurrency(sum.totalRevenue)}</h4>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
                )}

                {/* Details Table */}
                {selectedSegment && (
                    <Card className="border-0 shadow-sm animate__animated animate__fadeInUp">
                        <CardBody>
                            <Row className="mb-4 align-items-center">
                                <Col md={4}>
                                    <h5 className="card-title mb-0">Customer list <Badge color={getSegmentColor(selectedSegment)} className="ms-2">{selectedSegment}</Badge></h5>
                                </Col>
                                <Col md={8}>
                                    <div className="d-flex gap-2 justify-content-end">
                                        <div className="search-box">
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control rounded-pill"
                                                    placeholder="Search customer name..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={{ paddingLeft: '40px' }}
                                                />
                                                <Search className="position-absolute text-muted" size={18} style={{ left: '15px', top: '10px' }} />
                                            </div>
                                        </div>
                                        <Button color="light" size="sm" onClick={() => setSelectedSegment(null)}>Hide List</Button>
                                    </div>
                                </Col>
                            </Row>

                            <div className="table-responsive">
                                <Table hover className="align-middle table-nowrap mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Customer Name</th>
                                            <th>Segment</th>
                                            <th className="text-end">Visit Count</th>
                                            <th className="text-end">Lifetime Spend</th>
                                            <th>Last Visit</th>
                                            <th className="text-center">Days Since</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <ClipLoader color="#556ee6" />
                                                    <p className="mt-2 text-muted">Calculating segments...</p>
                                                </td>
                                            </tr>
                                        ) : filteredDetails.length > 0 ? (
                                            filteredDetails.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="avatar-xs me-3">
                                                                <span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-12">
                                                                    {item.customer_NameEn.charAt(0)}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <h5 className="font-size-14 mb-1">
                                                                    <a
                                                                        href="#"
                                                                        onClick={(e) => { e.preventDefault(); setSelectedCustomerId(item.customer_Id); setShowCustomerProfile(true); }}
                                                                        className="text-dark"
                                                                    >
                                                                        {item.customer_NameEn}
                                                                    </a>
                                                                </h5>
                                                                <p className="text-muted mb-0 font-size-12">{item.customer_ContactNo}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            color={getSegmentColor(item.segment)}
                                                            pill
                                                            className="font-size-11 px-2 py-1 text-dark"
                                                            style={{ color: '#000' }}
                                                        >
                                                            {item.segment}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end font-weight-bold">{item.visitCount}</td>
                                                    <td className="text-end text-success font-weight-bold">{formatCurrency(item.lifetimeSpend)}</td>
                                                    <td>{formatDate(item.lastVisitDate)}</td>
                                                    <td className="text-center">
                                                        <span className={item.daysSinceLastVisit > 120 ? "text-danger" : item.daysSinceLastVisit > 60 ? "text-warning" : "text-success"}>
                                                            {item.daysSinceLastVisit} days
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <div className="text-muted">No customers found matching the criteria.</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Customer Profile Modal */}
                <CustomerProfileModal
                    isOpen={showCustomerProfile}
                    toggle={() => {
                        setShowCustomerProfile(false);
                        setSelectedCustomerId(null);
                    }}
                    customerId={selectedCustomerId}
                />
            </Container>
        </div>
    );
}
